import { TrackJS } from 'trackjs';

import Browser from '@wetransfer/concorde-browser';

import { actions as notificationActions } from './notification';

import UI from './ui';

import API from '@app/api';
import ActionHelper from '@module/redux-actions';
import Analytics from '@module/analytics';

import { extractEmails } from '@module/transfer/build';

import Pending from '@app/action/pending';

import {
  REGEXP_EMAIL,
  REGEXP_BLANK,
  MAX_MESSAGE_SIZE_IN_BYTES,
} from '@module/transfer/config';

import Helper from '@app/helper';

export const actions = ActionHelper.types([
  'TRANSFER_PREPARE_DOWNLOAD',
  'TRANSFER_CREATE',
  'TRANSFER_CREATE_FORWARD',
  'TRANSFER_CREATE_SIMILAR',
  'TRANSFER_CHANGE_TYPE',
  'TRANSFER_CHANGE_EXPIRY',
  'TRANSFER_CHANGE_PASSWORD',
  'TRANSFER_SET_SENDER',
  'TRANSFER_SET_MESSAGE',
  'TRANSFER_SET_LANGUAGE',
  'TRANSFER_SET_DOMAIN_USER_ID',
  'TRANSFER_SET_CHANNEL',
  'TRANSFER_ADD_RECIPIENT',
  'TRANSFER_ADD_FILES',
  'TRANSFER_REMOVE_FILE',
  'TRANSFER_REMOVE_DIRECTORY',
  'TRANSFER_REMOVE_RECIPIENT',
  'TRANSFER_RESET',
  'TRANSFER_START',
  'TRANSFER_STATE_DOWNLOADABLE',
  'TRANSFER_STATE_DOWNLOAD_STARTING',
  'TRANSFER_STATE_DOWNLOADING',
  'TRANSFER_STATE_FINISHED',
  'TRANSFER_RESET_DOWNLOAD',
  'TRANSFER_SET_STATE',
  'TRANSFER_SET_EXPIRY_SECONDS',
  'TRANSFER_SET_SHORT_URL',
  'TRANSFER_SET_ID',

  // Download related
  'DOWNLOAD_PENDING',
  'DOWNLOAD_SUCCESS',
  'DOWNLOAD_ERROR',
  'TRANSFER_DOWNLOAD_EXPIRED',
  'TRANSFER_DOWNLOAD_INVALID',
  'TRANSFER_STATE_DOWNLOAD_STARTING_FILE',
  'TRANSFER_STATE_DOWNLOADING_FILE',
  'TRANSFER_STATE_FINISHED_FILE',

  'RECAPTCHA_CHALLENGE_RECEIVED',

  // Upload related
  'UPLOAD_PENDING',
  'UPLOAD_RESET',
  'UPLOAD_ERROR',
  'UPLOAD_ERROR_RESET',
  'UPLOAD_NEED_VERIFICATION',
  'UPLOAD_VERIFIED',

  // Preview related
  'PREVIEW_LOADING',
  'PREVIEW_LOADED',
  'PREVIEW_ERROR',

  // Feedback related
  'TRANSFER_STATE_FEEDBACK',
  'TRANSFER_STATE_FEEDBACK_COMPLETE',
]);

export function dispatchNotification(dispatch, transfer, channel, text) {
  const ignoredErrorMessages = [
    'LimitReached',
    'UserBlocked',
    'TransferFailed',
    'UnavailableParent',
    'TransferCanceled',
    'HumanVerificationRequired',
  ];
  if (ignoredErrorMessages.indexOf(text) !== -1) {
    return;
  }

  // NOTE: This is for the 20gb channel feature
  if (text === 'TransferTooBig') {
    const containsRecipient =
      transfer.recipients.length === 1 &&
      transfer.recipients[0] === channel.defaultRecipient;
    if (channel.defaultRecipient && !containsRecipient) {
      text = 'RequiresDefaultRecipient';
    }
  }

  // Dispatch the notification
  dispatch({ type: notificationActions.NOTIFICATION_TRANSFER_SET, text });
}

function makeTransferEventData(transfer, mode, isForwardedTransfer) {
  const eventData = {
    mode,
    type: transfer.type,
    item_count: transfer.items.length,
    app_type: Browser.isMobile ? 'mobile' : 'web',
    receiver_count: transfer.recipients.length,
  };

  switch (mode) {
    case 'upload':
      return {
        size: transfer.totalSize,
        forward: isForwardedTransfer,
        password: !!transfer.password,
        expiry: transfer.expiry,
        file_types: Array.from(new Set(transfer.items.map((f) => f.type))),
        duration: undefined,
        success: undefined,
        ...eventData,
      };
  }
}

export default ActionHelper.generate({
  create: [actions.TRANSFER_CREATE],
  createSimilar: [actions.TRANSFER_CREATE_SIMILAR, 'transfer'],
  changeType: [actions.TRANSFER_CHANGE_TYPE, 'transferType'],
  changeExpiry: [actions.TRANSFER_CHANGE_EXPIRY, 'period'],
  changePassword: [actions.TRANSFER_CHANGE_PASSWORD, 'password'],
  removeFile: [actions.TRANSFER_REMOVE_FILE, 'fileName'],
  removeDirectory: [actions.TRANSFER_REMOVE_DIRECTORY, 'directoryName'],
  removeRecipient: [actions.TRANSFER_REMOVE_RECIPIENT, 'email'],
  clearUploadError: [actions.UPLOAD_ERROR_RESET],
  displayError: [actions.UPLOAD_ERROR, 'error', 'field'],
  resetDownload: [actions.TRANSFER_RESET_DOWNLOAD],
  previewLoading: [actions.PREVIEW_LOADING, 'file_id', 'file_url'],
  previewLoaded: [actions.PREVIEW_LOADED, 'file_id'],
  previewError: [actions.PREVIEW_ERROR, 'file_id'],
  recaptcha: [actions.RECAPTCHA_CHALLENGE_RECEIVED, 'transfer_id'],
  showFeedback: [actions.TRANSFER_STATE_FEEDBACK],

  // Verify the download
  verifiedPassword(password) {
    return (dispatch) => {
      dispatch({ type: actions.TRANSFER_CHANGE_PASSWORD, password });
    };
  },

  // Validate and set the Transfer sender
  setSender(email) {
    return (dispatch) => {
      if (!email.replace(REGEXP_BLANK, '')) {
        dispatch({ type: actions.TRANSFER_SET_SENDER, email: '' });
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'MissingSender',
        });
      } else if (!email.match(REGEXP_EMAIL)) {
        dispatch({ type: actions.TRANSFER_SET_SENDER, email: '' });
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'InvalidSender',
        });
      }

      return dispatch({ type: actions.TRANSFER_SET_SENDER, email });
    };
  },

  // Validate and set the Transfer message
  setMessage(message) {
    const byteSize = unescape(encodeURIComponent(message)).length;

    if (byteSize > MAX_MESSAGE_SIZE_IN_BYTES) {
      return {
        type: notificationActions.NOTIFICATION_TRANSFER_SET,
        text: 'MessageTooLarge',
      };
    }

    // Check max length (bytes)
    return { type: actions.TRANSFER_SET_MESSAGE, message };
  },

  // Validate and add a Transfer recipient
  addRecipient(email) {
    return (dispatch, getState) => {
      const { transfer, user } = getState();
      const emails = extractEmails(email);

      // We only allow so many recipients for a transfer
      if (
        transfer.recipients.length + emails.length >
        user.rules.maximumNumberOfRecipients
      ) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'TooManyRecipients',
        });
      }

      // Nothing filled in
      if (!email.replace(REGEXP_BLANK, '') && emails.length === 0) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'MissingRecipients',
        });
      }

      // Doesn't match.... the regexp!
      if (!email.match(REGEXP_EMAIL) && emails.length <= 1) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'InvalidRecipient',
        });
      }

      return dispatch({ type: actions.TRANSFER_ADD_RECIPIENT, emails });
    };
  },

  // We cannot import the TransferUploadActions as this would create a
  // circular dependency between Action.Transfer and the TransferUploadActions.
  // So we pass the action as parameter and we call it here. This can go away
  // when the dependency of the Action.Transfer and TransferUploadActions is resolved.
  upload(
    startUploadProgress,
    stopUploadProgress,
    abortTransferAction,
    isForwardedTransfer
  ) {
    return async (dispatch, getState) => {
      const { transfer, channel } = getState();

      // Transfer data for Analytics
      const transferEventData = makeTransferEventData(
        transfer,
        'upload',
        isForwardedTransfer
      );
      // Is it the first time a user is uploading?
      if (Helper.getWTSent() === 0) {
        // then record the days since first upload
        transferEventData.days_since_first_upload = Helper.getDaysSinceFirstVisit();
      }

      Analytics.pushDataLayerEvent({
        category: 'Transfer',
        action: 'Upload',
        label: transfer.type,
        value: transfer.totalSize,
        transfer: transferEventData,
        user: {
          download_count: Helper.getWTDownloaded(),
          upload_count: Helper.getWTSent(),
          first_visit_date: Helper.getFirstVisitDate(),
        },
      });

      Analytics.startTimer('transfer.upload');

      // Start the Transfer, monitor changes and also
      // catch errors if they occur.
      try {
        dispatch(
          startUploadProgress(
            transfer.files,
            transfer.totalSize,
            transfer.uploadStartedAt
          )
        );

        dispatch({ type: actions.UPLOAD_ERROR_RESET });

        // Upload actual transfer
        await transfer.upload(
          () => dispatch(abortTransferAction(transfer)),
          isForwardedTransfer
        );

        Helper.incrementWTSent();

        transferEventData.success = true;
        transferEventData.cancelled = false;
        transferEventData.duration = Analytics.stopTimer('transfer.upload');
      } catch (error) {
        transferEventData.duration = Analytics.stopTimer('transfer.upload');
        transferEventData.success = false;
        transferEventData.cancelled = false;

        // Record error
        if (error === 'TransferCanceled') {
          transferEventData.cancelled = true;
          Analytics.pushDataLayerEvent({
            category: 'Transfer',
            action: 'Interrupted',
            label: transfer.type,
            value: transfer.totalSize,
            transfer: transferEventData,
          });
        }

        dispatch({ type: actions.UPLOAD_ERROR, error });
        dispatchNotification(dispatch, transfer, channel, error);
      } finally {
        dispatch(stopUploadProgress());

        dispatch({ type: actions.UPLOAD_RESET });
      }
    };
  },

  sendFeedback({ transferId, imageUrl, message, authorId }) {
    const pendingId = 'sendFeedback';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.Feedback.sendFeedbackForTransfer(
          { image_url: imageUrl, message, author_id: authorId },
          {
            pathExtValues: {
              transferId,
            },
          }
        );

        dispatch({ type: actions.TRANSFER_STATE_FEEDBACK_COMPLETE });
        dispatch(UI.setFeedbackSent());
      } catch (error) {
        dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'UnknownError',
        });

        TrackJS.track(error);
      } finally {
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },
});
